import { compact, take, truncate, uniqBy } from "lodash";
import moment from "moment";
import { useMemo, useState } from "react";
import { CitationFragmentFragment } from "types/graphql-schema";

import { assessmentTypeSectionHeadings } from "@apps/assessments/helpers";
import useLabel from "@apps/use-label/use-label";
import { currentOrganizationVar } from "@cache/cache";
import Avatar from "@components/avatar/avatar";
import Avatars from "@components/avatar/avatars";
import IntegrationLogo from "@components/integration-logo/integration-logo";
import Modal from "@components/modal/modal";
import ModalTitle from "@components/modal/modal-title";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { classNames } from "@helpers/css";
import { removeCurrentYear } from "@helpers/helpers";
import { pluralize, titleCase } from "@helpers/string";

const AskAICitationModal = ({
  citations,
  onClose,
}: {
  citations: CitationFragmentFragment[];
  onClose: () => void;
}) => {
  const label = useLabel();
  const organization = currentOrganizationVar();
  const reviewSourceProvider = label("review", { pluralize: true });
  const [filteredType, setFilteredType] = useState<string | null>(null);
  const compactedCitations = useMemo(
    () =>
      compact(citations).map((citation) => ({
        ...citation,
        sourceProvider:
          citation.sourceProvider === "topicflow" &&
          citation.sourceType === "past_review"
            ? reviewSourceProvider
            : citation.sourceProvider,
      })),
    [citations, reviewSourceProvider, organization],
  );
  const citationsWithUniqueSourceProviders = useMemo(
    () =>
      uniqBy(compactedCitations, "sourceProvider").filter(
        (type) => type.sourceProvider,
      ),
    [compactedCitations],
  );
  const filteredCitations = useMemo(
    () =>
      compactedCitations.filter((citation) =>
        filteredType ? citation.sourceProvider === filteredType : true,
      ),
    [compactedCitations, filteredType],
  );

  const filterTypeClassName =
    "border px-3 py-1 rounded-full text-gray-500 flex items-center gap-1";
  const filterTypeClassNameActive = "border-blue-100 bg-blue-50";
  const maxAvatarsForReviews = 2;
  const coupleAvatarsForReviews = take(
    compact(
      compactedCitations
        .filter((citation) => citation.sourceProvider === reviewSourceProvider)
        .map((citation) => citation.assessment?.responder),
    ),
    maxAvatarsForReviews,
  );

  return (
    <Modal alignedTop open>
      <div className="p-6 flex flex-col gap-6">
        <ModalTitle onClose={onClose}>
          {compactedCitations.length}{" "}
          {pluralize("source", compactedCitations.length)} Used
        </ModalTitle>
        {citationsWithUniqueSourceProviders.length > 0 && (
          <div className="flex gap-2">
            <button
              className={classNames(
                filterTypeClassName,
                filteredType === null ? filterTypeClassNameActive : "",
              )}
              onClick={() => setFilteredType(null)}
            >
              All
            </button>
            {citationsWithUniqueSourceProviders.map((sourceProvider) => (
              <button
                key={sourceProvider.sourceProvider}
                className={classNames(
                  filterTypeClassName,
                  filteredType === sourceProvider.sourceProvider
                    ? filterTypeClassNameActive
                    : "",
                )}
                onClick={() =>
                  setFilteredType(sourceProvider.sourceProvider || null)
                }
              >
                {sourceProvider.sourceProvider === reviewSourceProvider ? (
                  <Avatars
                    users={coupleAvatarsForReviews}
                    max={maxAvatarsForReviews + 1}
                    className="pl-[2px]"
                    size={4}
                    clickingWillShowModal={false}
                    showTooltip={false}
                  />
                ) : sourceProvider.sourceProvider ? (
                  <IntegrationLogo
                    provider={sourceProvider.sourceProvider}
                    className="mt-0.5 w-4 h-4"
                  />
                ) : (
                  <TopicflowIcon
                    type="externalLink"
                    className="mt-0.5 w-4 h-4"
                  />
                )}
                {titleCase(
                  sourceProvider.sourceProvider?.replace(/^merge_/, ""),
                )}
              </button>
            ))}
          </div>
        )}
        <div className="flex flex-col gap-4">
          {filteredCitations.map((citation) => (
            <div key={citation.url} className="flex items-start">
              {citation.sourceProvider === reviewSourceProvider &&
              citation.assessment?.responder ? (
                <Avatar
                  user={citation.assessment.responder}
                  className="float-left mr-1.5 mt-0.5 w-4 h-4"
                />
              ) : citation.sourceProvider ? (
                <IntegrationLogo
                  provider={citation.sourceProvider}
                  className="float-left mr-1.5 mt-0.5 w-4 h-4"
                />
              ) : (
                <TopicflowIcon
                  type="externalLink"
                  className="float-left mr-1.5 mt-0.5 w-4 h-4"
                />
              )}
              <a
                href={citation.url}
                className="text-gray-700 hover:underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                {citation.sourceProvider === reviewSourceProvider &&
                citation.assessment?.template.assessmentType
                  ? `${
                      assessmentTypeSectionHeadings(
                        label,
                        false,
                        citation.assessment?.target?.id ===
                          citation.assessment?.responder?.id,
                      )[`${citation.assessment.template.assessmentType}`]
                    }: `
                  : citation.sourceProvider === "topicflow"
                    ? `${label(citation.sourceType.split("_").join(" "), {
                        titleCase: true,
                      })}: `
                    : ""}
                {citation.sourceProvider === reviewSourceProvider &&
                citation.assessment?.responder?.name
                  ? citation.assessment.responder.name
                  : truncate(citation.title, { length: 100, separator: " " })}
                {citation.meeting && (
                  <span className="text-gray-400 ml-1">
                    (
                    {removeCurrentYear(
                      moment(citation.meeting.startDatetime).format("ll"),
                    )}
                    )
                  </span>
                )}
                {citation.assessment?.submittedDatetime && (
                  <span className="text-gray-400 ml-1">
                    (
                    {removeCurrentYear(
                      moment(citation.assessment.submittedDatetime).format(
                        "ll",
                      ),
                    )}
                    )
                  </span>
                )}
              </a>
            </div>
          ))}
          {filteredCitations.length === 0 && (
            <div className="text-gray-500">No citations found</div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default AskAICitationModal;
